import {
  registerOnboardingTours,
  getOnboardingTour,
  readOnboardingState,
  startOnboardingTour,
  advanceOnboardingTour,
  prevOnboardingTour,
  goToOnboardingStep,
  cancelOnboardingTour,
  tourMatchesRoute as engineTourMatchesRoute,
} from './onboardingEngine.js';

export const SETUP_TOURS = {
  'load-catalog': {
    id: 'load-catalog',
    title: 'Guía: Cargar tu catálogo',
    badge: 'Puesta a punto',
    startRoute: '/sistema/producto-nuevo',
    menuPath: 'Productos › Catálogo de productos',
    // Al terminar, el listado: es donde el usuario ve el resultado de lo que
    // acaba de cargar en vez de quedarse en el formulario vacio.
    finishRoute: '/sistema/productos',
    steps: [
      {
        route: '/sistema/progreso',
        title: '¡Te damos la bienvenida a tu catálogo!',
        description: 'Elegí la opción que te resulte más cómoda para ingresar tus productos al sistema:',
        options: [
          { label: 'Cargar a mano (Paso a paso)', action: 'next' },
          { label: 'Importar desde Excel (.xlsx)', action: 'switch-tour', tourId: 'load-catalog-excel' }
        ]
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-productos"], [data-onboarding-id="topbar-menu-item-productos-m"]',
        placement: 'bottom',
        title: 'Abrí "Productos" en el menú',
        description: 'En la barra de arriba, tocá "Productos".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-productos"], [data-onboarding-id="topbar-menu-subitem-productos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Catálogo de productos"',
        description: 'En el menú que se abrió, elegí "Catálogo de productos".',
      },
      {
        route: '/sistema/productos',
        selector: '[data-onboarding-id="productos-new-button"]',
        placement: 'left',
        title: 'Abrí el formulario de producto',
        description: 'En el catálogo, tocá "+ Nuevo" para crear tu primer producto.',
      },
      {
        route: '/sistema/producto-nuevo',
        selector: '[data-onboarding-id="product-name-field"]',
        placement: 'bottom',
        title: 'Nombre de tu producto',
        description: 'Escribí el nombre comercial con el que tus clientes identifican lo que vendés (ej: Coca Cola 500ml, Remera XL, Ensalada Caesar).',
      },
      {
        selector: '[data-onboarding-id="product-cost-field"]',
        placement: 'bottom',
        title: 'Costo de compra o elaboración',
        description: 'Ingresá cuánto pagás o te cuesta preparar este artículo. Nos sirve para calcular tu ganancia real en cada venta.',
      },
      {
        selector: '[data-onboarding-id="product-sell-field"]',
        placement: 'bottom',
        title: 'Precio de venta al público',
        description: 'Definí el precio final para tus clientes. Te mostraremos tu porcentaje de ganancia estimado al instante.',
      },
      {
        selector: '[data-onboarding-id="product-category-field"]',
        placement: 'bottom',
        title: 'Categoría del producto',
        description: 'Elegí a qué rubro pertenece. Los reportes agrupan las ventas por categoría: sin esto, todo lo que vendés cae en una sola bolsa.',
      },
      {
        selector: '[data-onboarding-id="product-brand-field"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Marca',
        description: 'Opcional, pero te deja filtrar por marca en el catálogo y en los reportes de rotación.',
      },
      {
        selector: '[data-onboarding-id="product-barcode-field"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Código de barras',
        description: 'Escaneá el código del envase o pegalo a mano. Con esto cobrás el producto pasándolo por el lector, sin buscarlo en la lista.',
      },
      {
        selector: '[data-onboarding-id="product-iva-field"]',
        placement: 'bottom',
        title: 'Alícuota de IVA',
        description: 'Viene en 21%. Cambiala solo si este producto tributa distinto: es lo que después sale discriminado en la factura.',
      },
      {
        selector: '[data-onboarding-id="product-min-stock-field"], input[name="min_stock"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Alerta de reposición de stock',
        description: 'Definí una cantidad mínima para enviarte un aviso automático antes de que te quedes sin unidades.',
      },
      {
        selector: '[data-onboarding-id="product-submit"]',
        placement: 'top',
        title: 'Guardar y dejar listo para vender',
        // El clic no prueba nada: el alta puede fallar (codigo de barras
        // duplicado -> 23505). La prueba es la marca que ProductoNuevo pinta
        // recien cuando el insert salio bien; hasta entonces el recorrido se
        // queda aca y el usuario puede corregir y reintentar.
        doneWhen: '[data-onboarding-id="product-save-success"]',
        doneHint: 'Tocá "Crear producto". Si el sistema rechaza el alta (por ejemplo, un código de barras repetido), corregí y volvé a intentar: la guía te espera acá.',
        description: 'Hacé clic aquí para guardar tu producto. ¡Ya va a quedar disponible para cobrarlo en tu caja o punto de venta!',
      }
    ]
  },
  'load-catalog-excel': {
    id: 'load-catalog-excel',
    title: 'Guía: Importar catálogo desde Excel',
    badge: 'Puesta a punto',
    startRoute: '/sistema/importaciones?tipo=PRODUCTOS',
    menuPath: 'Configuración › Importaciones',
    finishRoute: '/sistema/productos',
    steps: [
      {
        title: 'Carga masiva desde Excel',
        description: 'Te acompañamos paso a paso para importar todos tus productos desde tu planilla de forma rápida y sencilla.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-importaciones"], [data-onboarding-id="topbar-menu-subitem-importaciones-m"]',
        placement: 'bottom',
        title: 'Entrá a "Importaciones"',
        description: 'En el menú que se abrió, elegí "Importaciones".',
      },
      {
        // El Centro de Importacion abre en la grilla de tipos: el selector de
        // archivo solo existe DESPUES de elegir "Productos". Sin este paso el
        // recorrido buscaba un selector inexistente y el auto-recupero lo
        // devolvia al paso del menu (que siempre esta en el DOM), en loop.
        route: '/sistema/importaciones',
        selector: '[data-onboarding-id="import-type-PRODUCTOS"]',
        placement: 'bottom',
        title: 'Elegí "Productos (Inventario)"',
        // Se da por hecho cuando aparece el selector de archivo: es la prueba
        // de que el tipo quedo elegido (sirve igual si se entro por deep-link
        // /sistema/importaciones?tipo=PRODUCTOS y la grilla nunca se muestra).
        doneWhen: '[data-onboarding-id="import-excel-file-label"]',
        doneHint: 'Tocá la tarjeta "Productos (Inventario)". El recorrido sigue solo.',
        description: 'En el Centro de Importación elegí la tarjeta "Productos (Inventario)": es la que carga tu catálogo con costos, precios y stock.',
      },
      {
        selector: '[data-onboarding-id="import-excel-file-label"]',
        placement: 'bottom',
        title: 'Seleccionar tu planilla',
        // El clic abre el explorador de archivos y puede cancelarse, asi que no
        // prueba nada: el paso se da por hecho cuando aparece la pantalla de
        // mapeo, que es lo que solo existe si la planilla se leyo.
        doneWhen: '[data-onboarding-id="import-excel-mapping-container"]',
        doneHint: 'Tocá "Seleccionar Excel" y elegí tu archivo .xlsx. Apenas se lea, el recorrido sigue solo.',
        description: 'Tocá acá y elegí tu archivo .xlsx. Importante: la primera fila tiene que tener los títulos de las columnas (Nombre, Costo, Precio, Stock).',
      },
      {
        selector: '[data-onboarding-id="import-excel-mapping-container"]',
        placement: 'bottom-right',
        title: 'Asociar tus columnas',
        // Se completa al llegar a la previsualizacion: es la prueba de que las
        // columnas quedaron asociadas.
        doneWhen: '[data-onboarding-id="import-excel-preview-submit"]',
        doneHint: 'Elegí en cada campo qué columna de tu Excel le corresponde —"Nombre del producto" es obligatorio— y bajá hasta el final para tocar "Ver Previsualización". El recorrido sigue solo.',
        description: 'Cada tarjeta es un campo del sistema: elegí qué columna de tu Excel le corresponde. "Nombre del producto" es obligatorio; Costo, Precio y Categoría son los que más te conviene completar. Cuando termines, bajá y tocá "Ver Previsualización".',
      },
      {
        selector: '[data-onboarding-id="import-excel-preview-submit"]',
        placement: 'bottom',
        title: 'Confirmar e ingresar datos',
        // Solo se da por hecho cuando la importacion corrio de verdad y aparece
        // la pantalla de resultados.
        doneWhen: '[data-onboarding-id="import-excel-results-finish"]',
        doneHint: 'Revisá la lista y tocá "Confirmar Importación" para guardar los productos. Cuando termine, el recorrido sigue solo.',
        description: 'Revisá la lista detectada y tocá "Confirmar Importación" para guardar de verdad todos tus productos en el sistema.',
      },
      {
        selector: '[data-onboarding-id="import-excel-results-finish"]',
        placement: 'top',
        title: '¡Importación finalizada con éxito!',
        description: 'Tus productos se guardaron correctamente. Al finalizar la guía te llevamos al listado de productos para que veas todo lo que se cargó.',
      }
    ]
  },
  // Entre el catalogo y la primera venta: un producto recien creado nace con
  // stock 0 y el POS no lo deja vender (allow_negative_stock viene apagado en
  // comercio y kiosco). Sin este paso, la guia de primera venta arranca contra
  // una pared.
  'load-stock': {
    id: 'load-stock',
    title: 'Guía: Ingresá stock a tu depósito',
    badge: 'Puesta a punto',
    startRoute: '/sistema/inventario-ajuste',
    menuPath: 'Productos › Ajustes de stock',
    finishRoute: '/sistema/inventario',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Cargá las unidades que tenés',
        description: 'Tus productos ya están en el catálogo, pero todavía sin unidades. Con un ajuste de ingreso le decís al sistema cuánto tenés de cada uno: recién ahí podés venderlos y el stock se descuenta solo.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-productos"], [data-onboarding-id="topbar-menu-item-productos-m"]',
        placement: 'bottom',
        title: 'Abrí "Productos" en el menú',
        description: 'En la barra de arriba, tocá "Productos".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-inventario-ajuste"], [data-onboarding-id="topbar-menu-subitem-inventario-ajuste-m"]',
        placement: 'bottom',
        title: 'Entrá a "Ajustes de stock"',
        description: 'En el menú que se abrió, elegí "Ajustes de stock".',
      },
      {
        route: '/sistema/inventario-ajuste',
        selector: '[data-onboarding-id="stock-adjust-product-field"]',
        placement: 'bottom',
        title: 'Elegí el producto',
        description: 'Escribí el nombre, la marca o el código del producto al que le vas a cargar unidades y elegilo de la lista.',
      },
      {
        selector: '[data-onboarding-id="stock-adjust-site-field"]',
        placement: 'bottom',
        title: 'Elegí la sucursal',
        description: 'El stock vive por sucursal: indicá en cuál están físicamente las unidades que vas a ingresar.',
      },
      {
        selector: '[data-onboarding-id="stock-adjust-type-in"]',
        placement: 'bottom',
        title: 'Movimiento: Ingreso',
        description: 'Dejá seleccionado "Ingreso": estás sumando mercadería. "Egreso" es para roturas, vencimientos o consumo interno.',
      },
      {
        selector: '[data-onboarding-id="stock-adjust-qty-field"]',
        placement: 'bottom',
        title: 'Cantidad que tenés',
        description: 'Escribí cuántas unidades tenés hoy en el depósito o en la góndola. Es la cantidad desde la que el sistema va a empezar a descontar cada venta.',
      },
      {
        selector: '[data-onboarding-id="stock-adjust-submit"]',
        placement: 'left',
        title: 'Confirmar el ingreso',
        // El clic no prueba nada: el movimiento puede rechazarse. La prueba es
        // la marca que aparece cuando quedo registrado.
        doneWhen: '[data-onboarding-id="stock-adjust-success"]',
        doneHint: 'Tocá "Confirmar". Si falta el producto o la sucursal el botón queda deshabilitado: completá lo que falte y el recorrido sigue solo.',
        description: 'Confirmá el movimiento. El stock queda actualizado al instante y el ajuste queda registrado en el historial, con motivo y responsable.',
      }
    ]
  },
  'first-sale': {
    id: 'first-sale',
    title: 'Guía: Tu primera venta real',
    badge: 'Puesta a punto',
    startRoute: '/sistema/ventas-pos',
    menuPath: 'Ventas › Punto de venta',
    steps: [
      {
        title: 'Tu primera venta en la caja',
        description: 'Te mostramos lo ágil y fácil que es registrar una venta desde el punto de venta (POS) y actualizar tu stock.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-ventas"], [data-onboarding-id="topbar-menu-item-ventas-m"]',
        placement: 'bottom',
        title: 'Abrí "Ventas" en el menú',
        description: 'En la barra de arriba, tocá "Ventas".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-ventas-pos"], [data-onboarding-id="topbar-menu-subitem-ventas-pos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Punto de venta"',
        description: 'En el menú que se abrió, elegí "Punto de venta".',
      },
      {
        route: '/sistema/ventas-pos',
        selector: '[data-onboarding-id="ventas-pos-catalog"]',
        placement: 'bottom',
        title: 'Catálogo de selección rápida',
        description: 'Tocá los productos que querés vender para sumarlos directamente al pedido actual.',
      },
      {
        selector: '[data-onboarding-id="ventas-pos-checkout"]',
        placement: 'left',
        title: 'Iniciar el cobro',
        description: 'Cuando tengas los artículos cargados, presioná el botón verde Cobrar para elegir el medio de pago.',
      },
      {
        selector: '[data-onboarding-id="ventas-pos-method-select"]',
        placement: 'left',
        // El cartel no puede taparle el panel de cobro: es donde el usuario
        // tiene que ver el descuento, el medio de pago y el total mientras la
        // guia se los explica.
        avoid: '[data-onboarding-id="ventas-pos-checkout"]',
        title: 'Medio de pago elegido',
        description: 'Seleccioná cómo te abona tu cliente: Efectivo, Mercado Pago, Transferencia o Tarjeta.',
      },
      {
        selector: '[data-onboarding-id="ventas-pos-submit"]',
        placement: 'left',
        avoid: '[data-onboarding-id="ventas-pos-checkout"]',
        title: 'Confirmar y emitir comprobante',
        // La venta puede fallar (sin stock, sin caja abierta). El recorrido se
        // cierra cuando la venta quedo registrada de verdad, no con el clic.
        doneWhen: '[data-onboarding-id="ventas-pos-sale-success"]',
        doneHint: 'Tocá "Cobrar". Si algo lo impide —falta stock o la caja está cerrada— vas a ver el aviso en pantalla: resolvelo y volvé a intentar, la guía te espera acá.',
        description: 'Hacé clic aquí para completar el cobro. Se emitirá el ticket, tu stock se descontará y el dinero ingresará a caja.',
      }
    ]
  },
  'open-cash': {
    id: 'open-cash',
    title: 'Guía: Control de tu caja diaria',
    badge: 'Puesta a punto',
    startRoute: '/sistema/caja',
    menuPath: 'Ventas › Arqueo de caja',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Control de caja en efectivo',
        description: 'Mantené la tranquilidad de tu negocio llevando el registro claro de aperturas, ingresos y cierres de turno.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-ventas"], [data-onboarding-id="topbar-menu-item-ventas-m"]',
        placement: 'bottom',
        title: 'Abrí "Ventas" en el menú',
        description: 'En la barra de arriba, tocá "Ventas".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-caja"], [data-onboarding-id="topbar-menu-subitem-caja-m"]',
        placement: 'bottom',
        title: 'Entrá a "Arqueo de caja"',
        description: 'En el menú que se abrió, elegí "Arqueo de caja".',
      },
      {
        route: '/sistema/caja',
        selector: '[data-onboarding-id="caja-abrir-btn"], .btn-abrir-caja',
        placement: 'bottom',
        title: 'Abrir turno de caja',
        description: 'Hacé clic aquí al iniciar tu jornada para declarar el dinero en efectivo inicial de tu cajón.',
      },
      {
        selector: '[data-onboarding-id="caja-movimiento-btn"], .btn-registrar-movimiento',
        placement: 'bottom',
        optionalField: true,
        title: 'Registrar movimientos de efectivo',
        description: 'Con el turno abierto, usá esta función para anotar retiros o ingresos rápidos (como pago de envíos o cambio de billetes).',
      },
      {
        selector: '[data-onboarding-id="caja-cerrar-btn"], .btn-cerrar-caja',
        placement: 'top',
        optionalField: true,
        title: 'Cerrar caja y arqueo',
        description: 'Al finalizar el turno, contá el efectivo y confirmá que el arqueo coincida con las ventas del día.',
      }
    ]
  },
  'first-purchase': {
    id: 'first-purchase',
    title: 'Guía: Registrar una compra',
    badge: 'Puesta a punto',
    startRoute: '/sistema/compras-nueva',
    menuPath: 'Compras › Nueva compra',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Compras a proveedores',
        description: 'Registrá las compras de mercadería para actualizar automáticamente tu stock disponible y controlar tus costos. Si aún no cargaste tu proveedor, crealo en un clic:',
        options: [
          { label: 'Ya tengo proveedores (Continuar)', action: 'next' },
          { label: 'Crear un proveedor primero', action: 'navigate', route: '/sistema/proveedores' }
        ]
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-compras"], [data-onboarding-id="topbar-menu-item-compras-m"]',
        placement: 'bottom',
        title: 'Abrí "Compras" en el menú',
        description: 'En la barra de arriba, tocá "Compras".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-compras-nueva"], [data-onboarding-id="topbar-menu-subitem-compras-nueva-m"]',
        placement: 'bottom',
        title: 'Entrá a "Nueva compra"',
        description: 'En el menú que se abrió, elegí "Nueva compra".',
      },
      {
        route: '/sistema/compras-nueva',
        selector: '[data-onboarding-id="compras-proveedor-field"]',
        placement: 'top',
        title: 'Elegí el proveedor',
        description: 'Buscá y seleccioná la empresa a la que le compraste los artículos.',
      },
      {
        selector: '[data-onboarding-id="compras-product-field"]',
        placement: 'top',
        optionalField: true,
        title: 'Sumar productos comprados',
        description: 'Buscá cada producto por su nombre y agregalo a la lista de la compra.',
      },
      {
        selector: '[data-onboarding-id="compras-item-qty"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Cantidad recibida',
        description: 'Ingresá las unidades compradas: se ingresarán directamente a tu inventario al guardar.',
      },
      {
        selector: '[data-onboarding-id="compras-item-cost"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Costo unitario de compra',
        description: 'Verificá el costo pactado para que el sistema recalcule tu margen de ganancia real.',
      },
      {
        selector: '[data-onboarding-id="compras-submit"]',
        placement: 'top',
        optionalField: true,
        title: 'Confirmar y guardar compra',
        description: 'Al guardar, se incrementa tu stock, se refresca el costo unitario y se registra la cuenta si fue a plazo.',
      }
    ]
  },
  'business-data': {
    id: 'business-data',
    title: 'Guía: Personalizar tus comprobantes',
    badge: 'Puesta a punto',
    startRoute: '/sistema/empresa-datos',
    menuPath: 'Configuración › Mi empresa',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Datos de tu negocio',
        description: 'Cargá la información institucional de tu empresa para que figure impecable en todos tus tickets y facturas.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-empresa-datos"], [data-onboarding-id="topbar-menu-subitem-empresa-datos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Mi empresa"',
        description: 'En el menú que se abrió, elegí "Mi empresa".',
      },
      {
        route: '/sistema/empresa-datos',
        selector: '[data-onboarding-id="business-name-input"]',
        placement: 'bottom',
        title: 'Nombre o Razón Social',
        description: 'Escribí el nombre comercial con el que te conocen y confían tus clientes.',
      },
      {
        selector: '[data-onboarding-id="business-cuit-input"]',
        placement: 'bottom',
        title: 'Identificación fiscal (CUIT)',
        description: 'Tu CUIT oficial para emitir comprobantes legales y presupuestos.',
      },
      {
        selector: '[data-onboarding-id="business-address-input"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Dirección del local o fiscal',
        description: 'La ubicación física que se imprimirá al encabezado de cada comprobante.',
      },
      {
        selector: '[data-onboarding-id="business-submit"]',
        placement: 'top',
        title: 'Guardar información',
        description: 'Confirmá los cambios para actualizar la plantilla de tus recibos impresos y digitales.',
      }
    ]
  },
  'payment-methods': {
    id: 'payment-methods',
    title: 'Guia: Medios de cobro y acreditacion',
    badge: 'Puesta a punto',
    startRoute: '/sistema/metodos-cobro',
    menuPath: 'Configuración › Métodos de cobro',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Tus medios de cobro',
        description: 'Efectivo, transferencia, debito, credito y Mercado Pago ya vienen cargados con una comision estimada. Falta lo que solo vos sabes: a que cuenta te entra la plata y en cuantos dias.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-metodos-cobro"], [data-onboarding-id="topbar-menu-subitem-metodos-cobro-m"]',
        placement: 'bottom',
        title: 'Entrá a "Métodos de cobro"',
        description: 'En el menú que se abrió, elegí "Métodos de cobro".',
      },
      {
        route: '/sistema/metodos-cobro',
        selector: '[data-onboarding-id="payment-methods-list"]',
        placement: 'bottom',
        title: 'Abri el medio que quieras ajustar',
        description: 'Tocá una fila para editarla. Empeza por el que mas usas despues del efectivo (tarjeta de debito o Mercado Pago).',
      },
      {
        route: '/sistema/metodos-cobro-form',
        selector: '[data-onboarding-id="payment-method-bank-select"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Cuenta de destino de los fondos',
        description: 'Elegí el banco o billetera donde te acreditan estos cobros. Si todavía no cargaste cuentas bancarias, podés dejarlo para después.',
      },
      {
        route: '/sistema/metodos-cobro-form',
        selector: '[data-onboarding-id="payment-method-accreditation-input"]',
        placement: 'bottom',
        title: 'Dias de acreditacion',
        description: 'Cuantos dias tarda en entrar la plata (debito suele ser 1, credito 7 a 18). Con esto la proyeccion de caja deja de ser optimista.',
      },
      {
        route: '/sistema/metodos-cobro-form',
        selector: '[data-onboarding-id="payment-method-charge-input"]',
        placement: 'bottom',
        title: 'Comision real de tu contrato',
        description: 'Corregi el porcentaje que te cobra la procesadora. Esa comision se descuenta del margen en rentabilidad y en los reportes.',
      },
      {
        route: '/sistema/metodos-cobro-form',
        selector: '[data-onboarding-id="payment-method-submit"]',
        placement: 'bottom',
        title: 'Guardar el medio de cobro',
        description: 'Guarda y repeti con los demas medios electronicos. Con al menos uno vinculado a una cuenta, el paso queda listo.',
      }
    ]
  },
  'invite-team': {
    id: 'invite-team',
    title: 'Guía: Sumar a tu equipo',
    badge: 'Puesta a punto',
    startRoute: '/sistema/usuario-nuevo',
    menuPath: 'Configuración › Usuarios y roles',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Trabajar en equipo',
        description: 'Invitá a tus colaboradores con permisos específicos para operar el punto de venta de forma segura.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-permisos"], [data-onboarding-id="topbar-menu-subitem-permisos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Usuarios y roles"',
        description: 'En el menú que se abrió, elegí "Usuarios y roles".',
      },
      {
        route: '/sistema/permisos',
        selector: '[data-onboarding-id="user-new-btn"]',
        placement: 'left',
        title: 'Abrí el alta de usuario',
        description: 'En Usuarios y roles, tocá "+ Nuevo usuario" para invitar a alguien de tu equipo.',
      },
      {
        route: '/sistema/usuario-nuevo',
        selector: '[data-onboarding-id="team-email-input"]',
        placement: 'bottom',
        title: 'Correo de tu colaborador',
        description: 'Ingresá el mail donde recibirá la invitación de bienvenida para crear su contraseña.',
      },
      {
        selector: '[data-onboarding-id="team-name-input"]',
        placement: 'bottom',
        title: 'Nombre completo',
        description: 'Para saber exactamente quién realizó cada venta en el historial del sistema.',
      },
      {
        selector: '[data-onboarding-id="team-role-select"]',
        placement: 'bottom',
        title: 'Rol y permisos',
        description: 'Asigná el nivel adecuado: Vendedor (solo cobro), Cajero (cobros + cierres) o Administrador (acceso completo).',
      },
      {
        selector: '[data-onboarding-id="team-submit-btn"]',
        placement: 'top',
        title: 'Enviar acceso',
        description: 'Hacé clic aquí para enviar la invitación y registrar al nuevo integrante de tu equipo.',
      }
    ]
  },
  'configure-billing': {
    id: 'configure-billing',
    title: 'Guía: Facturación Electrónica ARCA',
    badge: 'Puesta a punto',
    startRoute: '/sistema/config-facturacion',
    menuPath: 'Configuración › Facturación (ARCA)',
    steps: [
      {
        title: 'Facturar con validez legal',
        description: 'Te acompañamos a conectar tu CUIT con ARCA para emitir facturas A, B y C directamente desde la caja.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-config-facturacion"], [data-onboarding-id="topbar-menu-subitem-config-facturacion-m"]',
        placement: 'bottom',
        title: 'Entrá a "Facturación (ARCA)"',
        description: 'En el menú que se abrió, elegí "Facturación (ARCA)".',
      },
      {
        selector: '[data-onboarding-id="afip-emisor-nuevo"]',
        placement: 'left',
        title: 'Alta de CUIT emisor',
        description: 'Presioná "Nuevo" para completar los datos fiscales de tu empresa.',
      },
      {
        selector: '[data-onboarding-id="afip-wizard-cuit"]',
        placement: 'bottom',
        title: 'CUIT autorizado',
        description: 'Ingresá los 11 dígitos del CUIT con el que vas a emitir tus comprobantes.',
      },
      {
        selector: '[data-onboarding-id="afip-wizard-pos"]',
        placement: 'bottom',
        title: 'Punto de venta ARCA',
        description: 'Declarás el número de punto de venta habilitado en la web de ARCA para webservices.',
      },
      {
        selector: '[data-onboarding-id="afip-wizard-cert"]',
        placement: 'top',
        title: 'Certificado y Clave (.crt / .key)',
        description: 'Cargá los archivos digitales otorgados por ARCA para autorizar la firma electrónica de tus facturas.',
      }
    ]
  },
  'load-recipes': {
    id: 'load-recipes',
    title: 'Guía: Recetas de cocina',
    badge: 'Puesta a punto',
    startRoute: '/sistema/recetas',
    menuPath: 'Producción › Recetas',
    steps: [
      {
        title: 'Recetas e insumos gastronómicos',
        description: 'Vinculá tus ingredientes a tus platos elaborados para descontar insumos automáticamente y conocer tu costo real.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-produccion"], [data-onboarding-id="topbar-menu-item-produccion-m"]',
        placement: 'bottom',
        title: 'Abrí "Producción" en el menú',
        description: 'En la barra de arriba, tocá "Producción".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-recetas"], [data-onboarding-id="topbar-menu-subitem-recetas-m"]',
        placement: 'bottom',
        title: 'Entrá a "Recetas"',
        description: 'En el menú que se abrió, elegí "Recetas".',
      },
      {
        selector: '[data-onboarding-id="recetas-product-select"]',
        placement: 'bottom',
        title: 'Seleccionar el plato final',
        description: 'Elegí el producto que vendés al público en tu menú (ej: Hamburguesa Doble, Pizza Especial).',
      },
      {
        selector: '[data-onboarding-id="recetas-insumo-select"]',
        placement: 'bottom',
        title: 'Elegir ingrediente',
        description: 'Seleccioná la materia prima consumida (ej: pan de hamburguesa, medallón de carne, queso fetas).',
      },
      {
        selector: '[data-onboarding-id="recetas-add-ingrediente"]',
        placement: 'left',
        title: 'Agregar a la receta',
        description: 'Indica la cantidad o gramaje por porción y sumalo al detalle del plato.',
      },
      {
        selector: '[data-onboarding-id="recetas-costo-resumen"]',
        placement: 'bottom',
        title: 'Costo total calculado',
        description: 'El sistema suma los insumos y te muestra el costo de producción exacto por porción.',
      },
      {
        selector: '[data-onboarding-id="recetas-submit"]',
        placement: 'top',
        title: 'Guardar receta',
        description: 'Al guardar, cada vez que cobres este plato en la caja se descontarán solos los ingredientes en stock.',
      }
    ]
  },
  'publish-web-store': {
    id: 'publish-web-store',
    title: 'Guía: Publicar Tienda Web',
    badge: 'Puesta a punto',
    startRoute: '/sistema/tienda-config',
    menuPath: 'Tienda › Config. tienda',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Catálogo digital y Tienda Web',
        description: 'Ofrecé tus productos en una tienda online propia para recibir pedidos directamente por WhatsApp o carrito de compras.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-tienda"], [data-onboarding-id="topbar-menu-item-tienda-m"]',
        placement: 'bottom',
        title: 'Abrí "Tienda" en el menú',
        description: 'En la barra de arriba, tocá "Tienda".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-tienda-config"], [data-onboarding-id="topbar-menu-subitem-tienda-config-m"]',
        placement: 'bottom',
        title: 'Entrá a "Config. tienda"',
        description: 'En el menú que se abrió, elegí "Config. tienda".',
      },
      {
        route: '/sistema/tienda-config',
        selector: '[data-onboarding-id="tienda-config-nombre"], [data-onboarding-id="tienda-nombre-input"]',
        placement: 'bottom',
        title: 'Nombre de tu Tienda',
        description: 'El título comercial con el que tus clientes verán tu catálogo digital en la web.',
      },
      {
        route: '/sistema/tienda-config',
        selector: '[data-onboarding-id="tienda-config-whatsapp"], [data-onboarding-id="tienda-whatsapp-input"]',
        placement: 'bottom',
        title: 'WhatsApp para recepción de pedidos',
        description: 'Ingresá tu número con código de área para recibir las solicitudes de tus clientes al instante.',
      },
      {
        route: '/sistema/tienda-config',
        selector: '[data-onboarding-id="tienda-config-submit"]',
        placement: 'top',
        title: 'Guardar y activar catálogo',
        description: 'Hacé clic en guardar para poner visible tu tienda y compartir tu enlace público.',
      }
    ]
  },
  'create-first-combo': {
    id: 'create-first-combo',
    title: 'Guía: Combos y Promociones',
    badge: 'Puesta a punto',
    startRoute: '/sistema/combo-builder',
    steps: [
      {
        title: 'Armar combos y promociones',
        description: 'Agrupá productos en promociones o packs de oferta para incentivar un ticket promedio más alto.',
      },
      {
        selector: '[data-onboarding-id="combo-name-input"]',
        placement: 'bottom',
        title: 'Nombre de la promoción',
        description: 'Ejemplo: Combo Desayuno 2x1, Pack Asado Completo, Promo Hamburguesa + Bebida.',
      },
      {
        selector: '[data-onboarding-id="combo-products-select"]',
        placement: 'bottom',
        title: 'Seleccionar artículos incluidos',
        description: 'Elegí los productos individuales que integran esta oferta especial.',
      },
      {
        selector: '[data-onboarding-id="combo-price-input"]',
        placement: 'bottom',
        title: 'Precio final del combo',
        description: 'Definí el valor promocional con el que cobrarás todo el paquete en la caja.',
      },
      {
        selector: '[data-onboarding-id="combo-submit-btn"]',
        placement: 'top',
        title: 'Guardar promoción',
        description: 'Tocá guardar para dejar disponible tu combo en el punto de venta.',
      }
    ]
  },
  'setup-client-credit': {
    id: 'setup-client-credit',
    title: 'Guía: Fiados y Cuentas Corrientes',
    badge: 'Puesta a punto',
    startRoute: '/sistema/clientes',
    menuPath: 'Clientes › Todos los clientes',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Gestión de cuentas corrientes',
        description: 'Llevá el saldo deudor de tus clientes habituales de forma prolija y sin perder ni un cuaderno.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-clientes"], [data-onboarding-id="topbar-menu-item-clientes-m"]',
        placement: 'bottom',
        title: 'Abrí "Clientes" en el menú',
        description: 'En la barra de arriba, tocá "Clientes".',
      },
      {
        route: '/sistema/clientes',
        selector: '[data-onboarding-id="client-new-btn"]',
        placement: 'left',
        title: 'Crear ficha de cliente',
        description: 'Hacé clic aquí para registrar a un cliente y asignarle un límite de crédito.',
      },
      {
        route: '/sistema/clientes-nuevo',
        selector: '[data-onboarding-id="client-name-input"]',
        placement: 'bottom',
        title: 'Nombre del cliente',
        description: 'Nombre y apellido o razón social para identificar la cuenta corriente.',
      },
      {
        route: '/sistema/clientes-nuevo',
        selector: '[data-onboarding-id="client-credit-limit-input"]',
        placement: 'bottom',
        title: 'Límite de fiado o crédito',
        description: 'Definí el monto máximo que puede quedar debiendo el cliente antes de solicitar un pago parcial.',
      },
      {
        route: '/sistema/clientes-nuevo',
        selector: '[data-onboarding-id="client-submit"]',
        placement: 'top',
        title: 'Guardar el cliente',
        description: 'Al guardar queda disponible en el punto de venta para cobrarle en cuenta corriente y ver su saldo.',
      }
    ]
  },
  'configure-price-lists': {
    id: 'configure-price-lists',
    title: 'Guía: Listas de Precios',
    badge: 'Puesta a punto',
    startRoute: '/sistema/listas-precios',
    menuPath: 'Ventas › Listas de precios',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Listas de precios diferenciadas',
        description: 'Manejá tarifas personalizadas para mostrador, clientes mayoristas o apps de despacho.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-ventas"], [data-onboarding-id="topbar-menu-item-ventas-m"]',
        placement: 'bottom',
        title: 'Abrí "Ventas" en el menú',
        description: 'En la barra de arriba, tocá "Ventas".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-listas-precios"], [data-onboarding-id="topbar-menu-subitem-listas-precios-m"]',
        placement: 'bottom',
        title: 'Entrá a "Listas de precios"',
        description: 'En el menú que se abrió, elegí "Listas de precios".',
      },
      {
        route: '/sistema/listas-precios',
        selector: '[data-onboarding-id="price-list-new-btn"]',
        placement: 'top',
        title: 'Crear nueva lista',
        description: 'Tocá aquí para agregar una lista de precios secundaria (ej: Mayorista, Pedidos Ya, Mostrador).',
      },
      {
        route: '/sistema/listas-precios/nueva',
        selector: '[data-onboarding-id="price-list-name-input"]',
        placement: 'bottom',
        title: 'Nombre de la lista',
        description: 'Asigná un título claro para identificarla rápidamente en el punto de venta.',
      },
      {
        route: '/sistema/listas-precios/nueva',
        selector: '[data-onboarding-id="price-list-margin-input"]',
        placement: 'bottom',
        title: 'Porcentaje de ajuste masivo',
        description: 'Podés definir un margen porcentual sobre el precio base para actualizar todos los artículos juntos.',
      },
      {
        route: '/sistema/listas-precios/nueva',
        selector: '[data-onboarding-id="price-list-submit"]',
        placement: 'top',
        title: 'Guardar la lista',
        description: 'Al guardar vas a poder elegirla en el punto de venta y en la tienda online sin tocar el precio base.',
      }
    ]
  },
  'setup-treasury-accounts': {
    id: 'setup-treasury-accounts',
    title: 'Guía: Cajas y Cuentas Bancarias',
    badge: 'Puesta a punto',
    startRoute: '/sistema/finanzas',
    steps: [
      {
        title: 'Orden de cajas y bancos',
        description: 'Separá tus saldos de efectivo, Mercado Pago y cuentas bancarias para conciliar todo sin esfuerzo.',
      },
      {
        selector: '[data-onboarding-id="treasury-account-new-btn"]',
        placement: 'top',
        title: 'Agregar cuenta financiera',
        description: 'Ingresá un nuevo canal de cobro para ordenar tus balances diarios.',
      },
      {
        selector: '[data-onboarding-id="treasury-account-name-input"]',
        placement: 'bottom',
        title: 'Nombre de la cuenta',
        description: 'Ejemplo: Mercado Pago Ventas, Banco Galicia Cta Cte, Caja Fuerte.',
      },
      {
        selector: '[data-onboarding-id="treasury-account-submit"]',
        placement: 'top',
        title: 'Guardar cuenta',
        description: 'Confirmá para vincular la cuenta a tus cierres de turno e ingresos de dinero.',
      }
    ]
  },
  'setup-multi-site-stock': {
    id: 'setup-multi-site-stock',
    title: 'Guía: Transferencias entre Sucursales',
    badge: 'Puesta a punto',
    startRoute: '/sistema/inventario-movimientos',
    steps: [
      {
        title: 'Movimientos de stock entre locales',
        description: 'Despachá mercadería desde tu depósito central a tus locales de venta sin perder el rastreo.',
      },
      {
        selector: '[data-onboarding-id="stock-transfer-new-btn"]',
        placement: 'top',
        title: 'Nueva transferencia',
        description: 'Tocá aquí para crear un remito de movimiento interno entre sucursales.',
      },
      {
        selector: '[data-onboarding-id="stock-transfer-origin-select"]',
        placement: 'bottom',
        title: 'Sucursal de origen',
        description: 'Seleccioná la sucursal o depósito desde el cual salen las unidades.',
      },
      {
        selector: '[data-onboarding-id="stock-transfer-dest-select"]',
        placement: 'bottom',
        title: 'Sucursal de destino',
        description: 'Seleccioná el local o punto de venta que va a recibir la mercadería.',
      },
      {
        selector: '[data-onboarding-id="stock-transfer-submit"]',
        placement: 'top',
        title: 'Confirmar y despachar',
        description: 'Al guardar, se descuentan las unidades en el origen y se suman en el destino automáticamente.',
      }
    ]
  },
  'review-profitability': {
    id: 'review-profitability',
    title: 'Guía: Análisis de Rentabilidad',
    badge: 'Análisis Comercial',
    startRoute: '/sistema/reportes',
    steps: [
      {
        title: 'Evaluación de rentabilidad y margen bruto',
        description: 'Conocé la ganancia real neta de tu negocio cruzando el precio de venta contra el costo de tu inventario.',
      },
      {
        selector: '[data-onboarding-id="reportes-date-range"]',
        placement: 'bottom',
        title: 'Filtro de fechas',
        description: 'Elegí el período que querés auditar (Hoy, Útimos 7 días, Este mes o un rango personalizado).',
      },
      {
        selector: '[data-onboarding-id="reportes-kpi-ganancia"]',
        placement: 'bottom',
        title: 'Ganancia bruta total',
        description: 'Te muestra el margen de utilidad limpio descontando el costo de compra o elaboración de tus productos.',
      },
      {
        selector: '[data-onboarding-id="reportes-kpi-margen"]',
        placement: 'bottom',
        title: 'Porcentaje de margen promedio',
        description: 'El rendimiento porcentual de tus ventas para saber si tus precios actuales cubren tus metas de rentabilidad.',
      },
      {
        selector: '[data-onboarding-id="reportes-export-btn"]',
        placement: 'left',
        title: 'Exportar informe',
        description: 'Descargá la planilla en Excel o CSV para analizar tus números con tu contador o administración.',
      }
    ]
  },
  'explore-reports': {
    id: 'explore-reports',
    title: 'Guía: Reportes y Métricas Comerciales',
    badge: 'Análisis Comercial',
    startRoute: '/sistema/reportes',
    steps: [
      {
        title: 'Tablero ejecutivo de ventas',
        description: 'Visualizá gráficos y tendencias para descubrir tus horarios pico, productos estrella y medios de cobro más usados.',
      },
      {
        selector: '[data-onboarding-id="reportes-tabs"]',
        placement: 'bottom',
        title: 'Secciones del reporte',
        description: 'Navegá entre Ventas por Producto, Medios de Pago, Rentabilidad y Categorías.',
      },
      {
        selector: '[data-onboarding-id="reportes-top-productos"]',
        placement: 'top',
        title: 'Ranking de los más vendidos',
        description: 'Identificá cuáles son los artículos que más ingresos generan a tu negocio.',
      },
      {
        selector: '[data-onboarding-id="reportes-medios-cobro"]',
        placement: 'top',
        title: 'Desglose por medio de cobro',
        description: 'Comprobá el volumen ingresado en Efectivo, Mercado Pago, Transferencia y Tarjeta.',
      }
    ]
  },
  'print-barcode-labels': {
    id: 'print-barcode-labels',
    title: 'Guía: Impresión de Etiquetas',
    badge: 'Puesta a punto',
    startRoute: '/sistema/inventario-etiquetas',
    steps: [
      {
        title: 'Etiquetado de artículos y gondolas',
        description: 'Generá e imprimí etiquetas con código de barras y precio actualizado para pegar en tus productos o estanterías.',
      },
      {
        selector: '[data-onboarding-id="etiquetas-product-select"]',
        placement: 'bottom',
        title: 'Elegir productos a etiquetar',
        description: 'Buscá los artículos de tu catálogo que querés incluir en la planilla de impresión.',
      },
      {
        selector: '[data-onboarding-id="etiquetas-copies-input"]',
        placement: 'bottom',
        title: 'Cantidad de etiquetas',
        description: 'Indica cuántas copias querés imprimir por cada producto seleccionado.',
      },
      {
        selector: '[data-onboarding-id="etiquetas-print-btn"]',
        placement: 'top',
        title: 'Generar vista previa e imprimir',
        description: 'Hacé clic para abrir el diálogo de impresión listo para tu impresora térmica o formato A4.',
      }
    ]
  },
  'stock-control': {
    id: 'stock-control',
    title: 'Guía: Ajustes y Arqueo de Inventario',
    badge: 'Operación Base',
    startRoute: '/sistema/inventario',
    steps: [
      {
        title: 'Control y arqueo físico de stock',
        description: 'Mantené la exactitud de tu inventario registrando ajustes por recuento físico, roturas o mermas.',
      },
      {
        selector: '[data-onboarding-id="inventario-search-input"]',
        placement: 'bottom',
        title: 'Buscador de stock',
        description: 'Buscá el artículo que querés ajustar por su nombre, código o categoría.',
      },
      {
        selector: '[data-onboarding-id="inventario-ajuste-btn"]',
        placement: 'left',
        title: 'Iniciar ajuste',
        description: 'Tocá el botón de ajuste rápido en el producto para ingresar la cantidad física contada.',
      },
      {
        selector: '[data-onboarding-id="inventario-ajuste-submit"]',
        placement: 'top',
        title: 'Guardar recuento',
        description: 'Confirmá el nuevo stock y el motivo del movimiento para actualizar tu inventario en tiempo real.',
      }
    ]
  },
  'load-suppliers': {
    id: 'load-suppliers',
    title: 'Guía: Cargar tus proveedores',
    badge: 'Puesta a punto',
    startRoute: '/sistema/proveedores',
    menuPath: 'Compras › Proveedores',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Tu cartera de proveedores',
        description: 'Con el proveedor cargado, cada compra actualiza sola el costo del producto y la cuenta corriente que le debés. Sin él, la compra queda como un gasto suelto.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-compras"], [data-onboarding-id="topbar-menu-item-compras-m"]',
        placement: 'bottom',
        title: 'Abrí "Compras" en el menú',
        description: 'En la barra de arriba, tocá "Compras".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-proveedores"], [data-onboarding-id="topbar-menu-subitem-proveedores-m"]',
        placement: 'bottom',
        title: 'Entrá a "Proveedores"',
        description: 'En el menú que se abrió, elegí "Proveedores".',
      },
      {
        route: '/sistema/proveedores',
        selector: '[data-onboarding-id="proveedor-new-btn"]',
        placement: 'left',
        title: 'Agregar proveedor',
        description: 'Empezá por el que más te vende. Después vas sumando el resto a medida que te llegan las facturas.',
      },
      {
        route: '/sistema/proveedores-nuevo',
        selector: '[data-onboarding-id="proveedor-name-input"]',
        placement: 'bottom',
        title: 'Razón social o nombre',
        description: 'Cargalo como figura en la factura: así lo vas a encontrar rápido cuando registres una compra.',
      },
      {
        route: '/sistema/proveedores-nuevo',
        selector: '[data-onboarding-id="proveedor-submit"]',
        placement: 'bottom',
        title: 'Guardar el proveedor',
        description: 'Al guardar queda disponible en el módulo de compras y con su cuenta corriente lista para registrar pagos.',
      }
    ]
  },
  'product-costs': {
    id: 'product-costs',
    title: 'Guía: Cargar el costo de tus productos',
    badge: 'Puesta a punto',
    startRoute: '/sistema/productos',
    menuPath: 'Productos › Catálogo de productos',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Sin costo no hay margen',
        description: 'El sistema calcula tu ganancia restando el costo al precio de venta. Los productos sin costo aparecen con margen cero y ensucian todos tus reportes de rentabilidad.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-productos"], [data-onboarding-id="topbar-menu-item-productos-m"]',
        placement: 'bottom',
        title: 'Abrí "Productos" en el menú',
        description: 'En la barra de arriba, tocá "Productos".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-productos"], [data-onboarding-id="topbar-menu-subitem-productos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Catálogo de productos"',
        description: 'En el menú que se abrió, elegí "Catálogo de productos".',
      },
      {
        route: '/sistema/productos',
        selector: '[data-onboarding-id="productos-search-input"]',
        placement: 'bottom',
        title: 'Buscá el producto',
        description: 'Escribí el nombre o el código del artículo que querés completar.',
      },
      {
        route: '/sistema/productos',
        selector: '[data-onboarding-id="productos-list"]',
        placement: 'top',
        title: 'Abrí el producto para editarlo',
        description: 'Tocá la fila del producto: se abre su ficha con precio, costo y stock.',
      },
      {
        route: '/sistema/producto-editar',
        selector: '[data-onboarding-id="product-cost-field"]',
        placement: 'bottom',
        optionalField: true,
        title: 'Costo de compra o elaboración',
        description: 'Cargá lo que te sale reponer una unidad, sin IVA si lo discriminás. El margen estimado se recalcula al instante arriba del precio.',
      },
      {
        route: '/sistema/producto-editar',
        selector: '[data-onboarding-id="product-submit"]',
        placement: 'top',
        title: 'Guardar y seguir',
        description: 'Repetí con los productos que más vendés: con esos ya vas a tener una lectura real de tu rentabilidad.',
      }
    ]
  },
  'product-categories': {
    id: 'product-categories',
    title: 'Guía: Clasificar tus productos',
    badge: 'Puesta a punto',
    startRoute: '/sistema/categorias',
    menuPath: 'Configuración › Categorías',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Por qué conviene categorizar',
        description: 'Los reportes agrupan las ventas por categoría. Sin clasificar, todo cae en una sola bolsa y no vas a poder ver qué rubro te deja plata y cuál no.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-config"], [data-onboarding-id="topbar-menu-item-config-m"]',
        placement: 'bottom',
        title: 'Abrí "Configuración" en el menú',
        description: 'En la barra de arriba, tocá "Configuración".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-categorias"], [data-onboarding-id="topbar-menu-subitem-categorias-m"]',
        placement: 'bottom',
        title: 'Entrá a "Categorías"',
        description: 'En el menú que se abrió, elegí "Categorías".',
      },
      {
        route: '/sistema/categorias',
        selector: '[data-onboarding-id="categoria-new-btn"]',
        placement: 'left',
        title: 'Crear una categoría',
        description: 'Armá pocas y claras: bebidas, almacén, limpieza, kiosco. Con 5 o 6 alcanza para leer bien los reportes.',
      },
      {
        route: '/sistema/categorias',
        selector: '[data-onboarding-id="categoria-name-input"]',
        placement: 'bottom',
        title: 'Nombre de la categoría',
        description: 'Usá el nombre con el que pensás tu negocio, no el del proveedor.',
      },
      {
        route: '/sistema/categorias',
        selector: '[data-onboarding-id="categoria-submit"]',
        placement: 'top',
        title: 'Guardar la categoría',
        description: 'Guardá y creá las que te falten antes de pasar a asignarlas.',
      },
      {
        route: '/sistema/productos',
        selector: '[data-onboarding-id="productos-list"]',
        placement: 'top',
        title: 'Asigná la categoría a tus productos',
        description: 'Abrí cada producto y elegí su categoría. El paso queda listo cuando tengas al menos un producto clasificado, pero conviene hacerlo con todo el catálogo.',
      }
    ]
  },
  'bank-accounts': {
    id: 'bank-accounts',
    title: 'Guía: Cuentas bancarias y billeteras',
    badge: 'Puesta a punto',
    startRoute: '/sistema/bancos',
    menuPath: 'Finanzas › Bancos',
    steps: [
      {
        route: '/sistema/progreso',
        title: 'Dónde cae tu plata',
        description: 'Cargá cada cuenta o billetera donde entra dinero (banco, Mercado Pago, Cuenta DNI). Es lo que después te deja conciliar transferencias y saber cuánto tenés disponible de verdad.',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-item-finanzas"], [data-onboarding-id="topbar-menu-item-finanzas-m"]',
        placement: 'bottom',
        title: 'Abrí "Finanzas" en el menú',
        description: 'En la barra de arriba, tocá "Finanzas".',
      },
      {
        navStep: true,
        selector: '[data-onboarding-id="topbar-menu-subitem-bancos"], [data-onboarding-id="topbar-menu-subitem-bancos-m"]',
        placement: 'bottom',
        title: 'Entrá a "Bancos"',
        description: 'En el menú que se abrió, elegí "Bancos".',
      },
      {
        route: '/sistema/bancos',
        selector: '[data-onboarding-id="banco-new-btn"]',
        placement: 'left',
        title: 'Nueva cuenta',
        description: 'Creá una por cada lugar donde te acreditan cobros. No mezcles la cuenta personal con la del negocio.',
      },
      {
        route: '/sistema/bancos-form',
        selector: '[data-onboarding-id="banco-name-input"]',
        placement: 'bottom',
        title: 'Nombre de la cuenta',
        description: 'Ejemplo: Banco Galicia Cta Cte, Mercado Pago Ventas. Con el nombre claro no te confundís al conciliar.',
      },
      {
        route: '/sistema/bancos-form',
        selector: '[data-onboarding-id="banco-submit"]',
        placement: 'top',
        title: 'Guardar la cuenta',
        description: 'Al guardar ya podés vincularla a tus medios de cobro para que la plata de las tarjetas caiga donde corresponde.',
      }
    ]
  }
};


// ---------------------------------------------------------------------------
// Estado — delegado en el motor único de onboarding
// ---------------------------------------------------------------------------
//
// Antes esta máquina de estado era propia: clave `impulza:setup-tour:v1`, solo
// localStorage, sin telemetría y sin forma de retomar un tour interrumpido. Al
// pasar al motor, los tours de puesta a punto ganan lo que ya tenía el
// recorrido de primera venta —eventos en `onboarding_events`, pausa y
// reanudación— sin tocar su contenido ni las pantallas que los consumen.


export const SETUP_TOUR_EVENT = 'impulza:setup-tour-change';
export const SETUP_TOUR_SET_STEP_EVENT = 'impulza:setup-tour-set-step';

const isBrowser = () => typeof window !== 'undefined';

// Los tours de puesta a punto se registran con su id tal cual (coincide con el
// id del paso del checklist "Empezá a operar", que es como los invocan las
// pantallas).
registerOnboardingTours(
  Object.values(SETUP_TOURS).map((tour) => ({ ...tour, kind: 'setup' })),
);

// Las pantallas siguen escuchando el evento histórico: se emite además del
// evento del motor para no tocar `Sistema.jsx` ni `ProgresoOnboarding.jsx`.
const emitLegacyChange = () => {
  if (isBrowser()) window.dispatchEvent(new CustomEvent(SETUP_TOUR_EVENT));
};

// Forma histórica del estado: `{ stepId, stepIndex, active }`, donde `stepId`
// es el id del tour (no el de un paso).
const toLegacyTourState = (state) => {
  if (!state || state.status !== 'active') return null;
  const tour = getOnboardingTour(state.tourId);
  if (!tour || tour.kind !== 'setup') return null;
  return { stepId: tour.id, stepIndex: state.stepIndex, active: true };
};

export function startSetupTour(stepId) {
  if (!getOnboardingTour(stepId)) return;
  // Si el mismo tour ya esta activo, NO lo reiniciamos: un disparo repetido de
  // startSetupTour (remount del cartel al navegar, doble click, re-render)
  // pisaba el progreso en curso y devolvia al usuario al paso 1. `fresh` solo
  // tiene sentido para arrancar de cero: primera vez, o "Repetir" sobre una
  // guia ya completada/abandonada (status distinto de 'active').
  const current = readOnboardingState();
  if (current?.tourId === stepId && current?.status === 'active') {
    emitLegacyChange();
    return;
  }
  // `fresh`: una guia se puede repetir. Sin esto, el estado terminado de la
  // vez anterior se reutilizaba y la guia no volvia a arrancar de cero.
  startOnboardingTour({ tourId: stepId, fresh: true });
  emitLegacyChange();
}

export function readSetupTour() {
  return toLegacyTourState(readOnboardingState());
}

export function advanceSetupTour() {
  if (!readSetupTour()) return;
  // Al pasar el último paso, el motor marca el tour como completado (antes se
  // borraba el estado): `readSetupTour` deja de devolverlo igual, y además
  // queda el hito `tour_completed` en el embudo.
  advanceOnboardingTour();
  emitLegacyChange();
}

export function setSetupTourStep(stepIndex) {
  const current = readSetupTour();
  if (!current) return;
  const next = goToOnboardingStep(stepIndex);
  emitLegacyChange();
  if (isBrowser() && next) {
    window.dispatchEvent(new CustomEvent(SETUP_TOUR_SET_STEP_EVENT, { detail: next.stepIndex }));
  }
}

export function prevSetupTour() {
  if (!readSetupTour()) return;
  prevOnboardingTour();
  emitLegacyChange();
}

/**
 * Un tour de puesta a punto solo tiene sentido en su propia pantalla: sus
 * selectores viven ahí. Si el usuario navega a otra ruta con el tour activo, el
 * cartel se seguiría renderizando sin poder resaltar nada (queda centrado, sin
 * foco) y además tapa el recorrido guiado de primera venta.
 */
export function setupTourMatchesRoute(definition, pathname) {
  if (!definition) return false;
  if (!pathname) return true;

  const current = readSetupTour();
  const stepIndex = current && current.stepId === definition.id ? current.stepIndex : 0;

  // La pantalla de progreso es el punto de partida de estos tours: no se
  // esconde el cartel mientras el usuario todavía está ahí.
  if (pathname === '/sistema/progreso' || pathname === '/sistema/progreso-onboarding') {
    const step = getOnboardingTour(definition.id)?.steps?.[stepIndex];
    if (step && !step.selector) return true;
  }

  // Guía que enseña el camino: el cartel acompaña al usuario mientras navega
  // por el menú hasta la pantalla, en vez de desaparecer apenas sale de la
  // ruta de inicio. Vale en la bienvenida y en los pasos que señalan la barra
  // de navegación, que existe en todas las pantallas del sistema.
  if (definition.menuPath && stepIndex === 0) return true;
  if (getOnboardingTour(definition.id)?.steps?.[stepIndex]?.navStep) return true;

  return engineTourMatchesRoute(getOnboardingTour(definition.id), pathname, stepIndex);
}

export function cancelSetupTour() {
  if (!readSetupTour()) return;
  cancelOnboardingTour();
  emitLegacyChange();
}
